<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Settings</div>
                <div class="page-bar-main">Edit Links</div>
            </div>
            <div style="display:flex">
                <div @click="() => { editMode = !editMode }" class="add-btn" style="margin:0"><span v-if="editMode">Close Edit Mode</span><span v-else>Edit Mode</span></div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <settings-layout desc="Links appear in the navigation menu in the Docs & Links section. Turn on Edit Mode to change them; drag a link by its handle to reorder.">
                <div class="form-wrapper settings-form" :style="editMode ? '' : 'pointer-events:none'">
                    <draggable :list="formData.teamHubLinks" handle=".crm-checklist-handle" :animation="150" ghost-class="kanban-ghost">
                        <section v-for="(link, i) in formData.teamHubLinks" :key="i" :class="['settings-card', 'thl-card', { 'thl-locked': !editMode }]">
                            <div class="thl-head">
                                <span class="crm-checklist-handle" title="Drag to reorder" aria-hidden="true">&#8942;&#8942;</span>
                                <input type="text" v-model="link.name" class="thl-name" placeholder="Link Name" aria-label="Link name" />
                                <button type="button" class="form-close-x" title="Remove link" :aria-label="'Remove ' + (link.name || 'link')" @click="removeLink(i)">&times;</button>
                            </div>
                            <div class="thl-row">
                                <label>
                                    <span class="thl-label">Type</span>
                                    <select v-model="link.type">
                                        <option value="embed">Embedded Pop Up</option>
                                        <option value="internal">Page Link</option>
                                        <option value="newtab">External Link</option>
                                        <option value="table">Sheet</option>
                                    </select>
                                </label>
                                <label>
                                    <span class="thl-label">Who sees it</span>
                                    <select v-model="link.userType">
                                        <option :value="undefined">All Users</option>
                                        <option value="nopublicusers">No Standard Users</option>
                                        <option value="teamhubadminonly">Team Hub Admin Only</option>
                                    </select>
                                </label>
                            </div>
                            <template v-if="link.type != 'table'">
                                <label>
                                    <span class="thl-label">Link URL</span>
                                    <input type="text" v-model="link.link" placeholder="https://..." @input="checkLink(link)" />
                                </label>
                                <drop-file @fileChange="onFileChange($event, link)" class="slim-drop-file thl-file" />
                            </template>
                            <div class="thl-label">Locations</div>
                            <div class="chip-editor-item-opts thl-locs">
                                <label v-for="(loc, j) in $store.state.user.locations" :key="'l'+j" class="checkbox-input">
                                    <input type="checkbox" v-model="link.locations[loc]" />
                                    <span>{{ loc }}</span>
                                </label>
                            </div>
                        </section>
                    </draggable>
                    <p v-if="!formData.teamHubLinks.length" class="settings-desc">No links yet.</p>
                    <div class="btn-row settings-save">
                        <button type="button" class="btn-lg btn-light" @click="addLink()">Add Link</button>
                        <button type="button" class="btn-lg" :disabled="disableButton" @click="submit()">Save Changes</button>
                    </div>
                </div>
            </settings-layout>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import { VueDraggableNext } from 'vue-draggable-next';
import DropFile from '@/components/DropFile.vue';
import SettingsLayout from '@/components/SettingsLayout.vue';

export default defineComponent({
    name: 'ConfigurationView',
    components: {
        draggable: VueDraggableNext,
        DropFile,
        SettingsLayout,
    },
    data() {
        return {
            formData: {
                teamHubLinks: [] as any
            },
            disableButton: false,
            editMode: false
        }
    },
    async created() {
        if(this.$store.state.user.teamHubLinks) {
            this.formData.teamHubLinks = JSON.parse(JSON.stringify(this.$store.state.user.teamHubLinks));
        }
        // Links saved before locations existed were visible everywhere, so start them with every location checked
        for(const link of this.formData.teamHubLinks) {
            if(!link.locations) {
                link.locations = this.allLocations();
            }
        }
        this.$store.state.routeLoaded = true;
    },
    methods: {
        async submit() {
            for(const link of this.formData.teamHubLinks) {
                if(!link.name) {
                    alert('Include a name for each link, or remove that link');
                    return;
                }
                if(!this.$store.state.user.locations.some((loc: string) => link.locations[loc])) {
                    alert(`Select at least one location for ${link.name}`);
                    return;
                }
                // do not check for link if table
                if(link.type == 'table') {
                    continue;
                }
                if(!link.link) {
                    alert('Include a URL for each link, or remove that link');
                    return;
                }
                if(link.type == 'internal') {
                    link.link = link.link.replace(window.location.origin, '');
                    if(link.link.includes('https://') || link.link.includes('http://')) {
                        alert(`${link.name} is not a valid page link, make sure this link is a link within your Ironbook, or change to an external link. The link domain should be from this page: ${window.location.origin}`);
                        return;
                    }
                } else if(!link.link.includes('https://') && !link.link.includes('http://') && link.link.includes('.')) {
                    link.link = 'https://' + link.link;
                }
            }

            this.disableButton = true;

            await axios.post(`${this.$store.state.apiUrl}/api/teamhublinks`, this.formData)
                .then(resp => {
                    alert('Links have been updated');
                    this.$store.state.user.teamHubLinks = resp.data.updatedLinks;
                    this.formData.teamHubLinks = JSON.parse(JSON.stringify(resp.data.updatedLinks));
                })
                .catch(e => {
                    alert('There was an error')
                    console.log(e);
                })

            this.disableButton = false;
        },
        checkLink(link: any) {
            if(link.link) {
                link.link = this.convertVideoEmbedLink(link.link);
            }
        },
        async onFileChange(file: any, link: any) {
            if(file) {
                this.disableButton = true;
                const formData = new FormData();
                formData.append('file', file);
                await axios.post(`${this.$store.state.apiUrl}/api/playbook/processimage`, formData)
                    .then(resp => {
                        link.link = this.$store.state.spacesUrl + resp.data.key;
                    })
                    .catch(e => {
                        console.log(e);
                        alert("There was an error.");
                    })
                this.disableButton = false;
            }
        },
        addLink() {
            this.formData.teamHubLinks.push({
                name: '',
                link: '',
                type: 'embed',
                locations: this.allLocations()
            })
        },
        allLocations() {
            const locations: any = {};
            for(const loc of this.$store.state.user.locations || []) {
                locations[loc] = true;
            }
            return locations;
        },
        removeLink(i: number) {
            this.formData.teamHubLinks.splice(i, 1);
        }
    }
});
</script>
